import { useTranslation } from "react-i18next";
import { Input, SearchableCombobox } from "@nexus/ui";
import { listTimezones } from "@/lib/timezones";
import {
  minuteToTimeValue,
  timeValueToMinute,
} from "@/features/changelog/lib/changelog-settings";
import { NumberField, SettingRow, SettingSwitch, SettingsCard } from "../shared/SettingsCard";
import { LIMITS } from "./changelog-settings-limits";
import type { ChangelogSettingsCardProps } from "./types";

/**
 * Whether a human sees the notes before the team's Slack channel does, and what
 * an unattended post waits for.
 */
export function ReleasePostsCard({
  draft,
  set,
  isReadOnly,
  isSlackConfigured,
}: ChangelogSettingsCardProps) {
  const { t } = useTranslation();
  const autoPublishes = !draft.requireDeployApproval;
  const isQuietWindowEmpty =
    draft.quietHoursEnabled && draft.quietHoursStartMinute === draft.quietHoursEndMinute;

  return (
    <SettingsCard
      icon="shield"
      title={t("admin.changelog.settings.approval.title", "Release posts to Slack")}
      description={t(
        "admin.changelog.settings.approval.description",
        "Whether a human sees the notes before the team's Slack channel does, and what an unattended post waits for.",
      )}
    >
      <SettingRow
        htmlFor="require-deploy-approval"
        label={t(
          "admin.changelog.settings.requireDeployApproval",
          "Approve each release before it posts",
        )}
        description={t(
          "admin.changelog.settings.requireDeployApprovalHelp",
          "On, notes wait in Draft until somebody approves them. Off, they post to Slack on their own once they are written.",
        )}
        control={
          <SettingSwitch
            id="require-deploy-approval"
            checked={draft.requireDeployApproval}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("requireDeployApproval", checked)}
          />
        }
      />

      {autoPublishes && !isSlackConfigured ? (
        <div className="px-5 py-3">
          <p className="m-0 rounded-md border border-warning-border bg-warning-subtle px-3 py-2 text-xs text-warning-subtle-foreground">
            {t(
              "admin.changelog.settings.noSlack",
              "No Slack webhook is configured, so nothing posts anywhere until one is set.",
            )}
          </p>
        </div>
      ) : null}

      <SettingRow
        htmlFor="auto-publish-delay"
        isNested
        isMuted={!autoPublishes}
        label={t("admin.changelog.settings.autoPublishDelay", "Hold an unattended post for")}
        description={t(
          "admin.changelog.settings.autoPublishDelayHelp",
          "A window to catch a bad note before anyone reads it. Zero posts as soon as the notes are ready.",
        )}
        control={
          <NumberField
            id="auto-publish-delay"
            value={draft.autoPublishDelayMinutes}
            min={LIMITS.autoPublishDelayMinutes.min}
            max={LIMITS.autoPublishDelayMinutes.max}
            unit={t("admin.changelog.settings.minutes", "minutes")}
            disabled={isReadOnly || !autoPublishes}
            onChange={(value) => set("autoPublishDelayMinutes", value)}
          />
        }
      />

      <SettingRow
        htmlFor="min-items"
        isNested
        isMuted={!autoPublishes}
        label={t("admin.changelog.settings.minItems", "Post only releases with at least")}
        description={t(
          "admin.changelog.settings.minItemsHelp",
          "A release with fewer user-facing changes than this waits for a human, so the channel is not spent on a dependency bump.",
        )}
        control={
          <NumberField
            id="min-items"
            value={draft.minItemsToAutoPublish}
            min={LIMITS.minItemsToAutoPublish.min}
            max={LIMITS.minItemsToAutoPublish.max}
            unit={t("admin.changelog.settings.changes", "changes")}
            disabled={isReadOnly || !autoPublishes}
            onChange={(value) => set("minItemsToAutoPublish", value)}
          />
        }
      />

      <SettingRow
        htmlFor="quiet-hours"
        isNested
        isMuted={!autoPublishes}
        label={t("admin.changelog.settings.quietHours", "Stay quiet overnight")}
        description={t(
          "admin.changelog.settings.quietHoursHelp",
          "Posts due inside the window are held until it ends. Approving by hand still posts immediately.",
        )}
        control={
          <SettingSwitch
            id="quiet-hours"
            checked={draft.quietHoursEnabled}
            disabled={isReadOnly || !autoPublishes}
            onCheckedChange={(checked) => set("quietHoursEnabled", checked)}
          />
        }
      />

      <SettingRow
        isNested
        isMuted={!autoPublishes || !draft.quietHoursEnabled}
        label={t("admin.changelog.settings.quietWindow", "Quiet between")}
        description={
          isQuietWindowEmpty
            ? t(
                "admin.changelog.settings.quietWindowEqual",
                "Start and end must differ — a window of zero length says both nothing and everything.",
              )
            : t(
                "admin.changelog.settings.quietWindowHelp",
                "A start later than the end wraps past midnight.",
              )
        }
        control={
          <div className="flex items-center gap-2">
            <Input
              type="time"
              aria-label={t("admin.changelog.settings.quietStart", "Quiet hours start")}
              value={minuteToTimeValue(draft.quietHoursStartMinute)}
              disabled={isReadOnly || !autoPublishes || !draft.quietHoursEnabled}
              onChange={(event) => {
                const minute = timeValueToMinute(event.target.value);
                if (minute !== null) set("quietHoursStartMinute", minute);
              }}
              className="h-8 w-28 text-xs tabular-nums"
            />
            <span className="text-xs text-muted-foreground">
              {t("admin.changelog.settings.and", "and")}
            </span>
            <Input
              type="time"
              aria-label={t("admin.changelog.settings.quietEnd", "Quiet hours end")}
              value={minuteToTimeValue(draft.quietHoursEndMinute)}
              disabled={isReadOnly || !autoPublishes || !draft.quietHoursEnabled}
              onChange={(event) => {
                const minute = timeValueToMinute(event.target.value);
                if (minute !== null) set("quietHoursEndMinute", minute);
              }}
              className="h-8 w-28 text-xs tabular-nums"
            />
          </div>
        }
      />

      <SettingRow
        isNested
        isMuted={!autoPublishes || !draft.quietHoursEnabled}
        label={t("admin.changelog.settings.timezone", "Read those hours in")}
        description={t(
          "admin.changelog.settings.timezoneHelp",
          "The zone the team keeps, which is rarely the server's.",
        )}
        control={
          <div className="w-56">
            <SearchableCombobox
              items={listTimezones(draft.timezone)}
              getKey={(zone) => zone}
              getLabel={(zone) => zone}
              value={draft.timezone}
              disabled={isReadOnly || !autoPublishes || !draft.quietHoursEnabled}
              ariaLabel={t("admin.changelog.settings.timezone", "Read those hours in")}
              searchPlaceholder={t("admin.changelog.settings.timezoneSearch", "Search zones…")}
              triggerClassName="h-8 text-xs"
              onSelect={(zone) => set("timezone", zone)}
            />
          </div>
        }
      />
    </SettingsCard>
  );
}
